import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import type { SectionId } from './registry'
import { settingsWords as words } from './words'

export interface ColumnRow { id: SectionId; to: string; current: boolean }
/** The rows under one heading: yours, the workspace's, or one project's. */
export interface ColumnGroup { key: string; label: string; rows: readonly ColumnRow[] }

/**
 * The sheet's one way to move (SUN-1454): every section, under the heading
 * of what it belongs to. It is one stop for the Tab key; from a row the
 * arrows, Home and End move along the rows, and Return opens the row.
 */
export function SettingsColumn({ groups, query, onQuery, onChoose, onClose }: {
  groups: readonly ColumnGroup[]
  query: string
  onQuery: (query: string) => void
  onChoose: (to: string) => void
  onClose: () => void
}) {
  const id = useId()
  const list = useRef<HTMLDivElement>(null)
  const rows = groups.flatMap(group => group.rows)
  const current = rows.find(row => row.current)?.to
  // The row the Tab key stops on: the last one the arrows reached, else the section open, else the first.
  const [reached, setReached] = useState<string | null>(null)
  const stop = rows.find(row => row.to === reached)?.to ?? current ?? rows[0]?.to
  useEffect(() => setReached(null), [current])
  useLayoutEffect(() => {
    list.current?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest' })
  }, [current])

  const links = () => [...(list.current?.querySelectorAll<HTMLElement>('.settings-row') ?? [])]
  function step(event: KeyboardEvent<HTMLElement>) {
    const all = links()
    const at = all.indexOf(document.activeElement as HTMLElement)
    const last = all.length - 1
    const to = event.key === 'ArrowDown' ? Math.min(at + 1, last) : event.key === 'ArrowUp' ? Math.max(at - 1, 0)
      : event.key === 'Home' ? 0 : event.key === 'End' ? last : null
    if (to === null || at < 0) return
    event.preventDefault()
    all[to]?.focus()
  }
  return <nav className="settings-column" aria-label="Settings">
    <div className="settings-column-top">
      <div className="settings-column-title"><span>Settings</span><button type="button" className="button settings-column-done" onClick={onClose}>{words.done}</button></div>
      <label className="settings-find"><span className="visually-hidden">{words.find}</span>
        <input type="search" placeholder={words.find} value={query} onChange={event => onQuery(event.target.value)}
          onKeyDown={event => { if (event.key === 'ArrowDown' && rows.length) { event.preventDefault(); links()[0]?.focus() } }} />
      </label>
    </div>
    <div className="settings-column-list" ref={list} onKeyDown={step}>
      {rows.length === 0 && <p className="muted" role="status">{words.none}</p>}
      {groups.map((group, index) => <div key={group.key} className="settings-column-group">
        {group.label && <div className="settings-column-heading" id={`${id}-${index}`}>{group.label}</div>}
        <ul aria-labelledby={group.label ? `${id}-${index}` : undefined}>
          {group.rows.map(row => <li key={row.to}>
            <Link className="settings-row" to={row.to} aria-current={row.current ? 'page' : undefined} tabIndex={row.to === stop ? 0 : -1}
              onClick={() => onChoose(row.to)} onFocus={() => setReached(row.to)}>
              <span>{words.entries[row.id].label}</span>
            </Link>
          </li>)}
        </ul>
      </div>)}
    </div>
  </nav>
}
